import React, { useRef } from 'react';
import { Box, Button, Step, StepLabel, Stepper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { OnboardingState, OnboardingAction } from './onboarding.types';
import Step1_SchoolProfile from './steps/Step1_SchoolProfile';
import Step2_AcademicTerms from './steps/Step2_AcademicTerms';
import Step3_Classrooms from './steps/Step3_Classrooms';
import Step4_FeeStructure from './steps/Step4_FeeStructure';
import Step5_AdminAccounts from './steps/Step5_AdminAccounts';
import Step6_Review from './steps/Step6_Review';

const SchoolProfileStep = Step1_SchoolProfile;
const AcademicTermsStep = Step2_AcademicTerms;
const ClassroomsStep = Step3_Classrooms;
const FeeStructureStep = Step4_FeeStructure;
const AdminAccountsStep = Step5_AdminAccounts;
const ReviewStep = Step6_Review;

interface OnboardingWizardProps {
  state: OnboardingState;
  dispatch: React.Dispatch<OnboardingAction>;
  onComplete: () => void;
  submitting?: boolean;
}

const STEP_LABELS = [
  'onboarding.step_school_profile',
  'onboarding.step_academic_terms',
  'onboarding.step_classrooms',
  'onboarding.step_fee_structure',
  'onboarding.step_admin_accounts',
  'onboarding.step_review',
];

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  state,
  dispatch,
  onComplete,
  submitting = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const formikRef = useRef<FormikProps<any>>(null);

  const goToStep = (step: number) => dispatch({ type: 'SET_STEP', payload: step });

  const handleNext = async () => {
    if (formikRef.current) {
      const errors = await formikRef.current.validateForm();
      if (Object.keys(errors).length > 0) {
        formikRef.current.setTouched(
          Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
        );
        return;
      }
      formikRef.current.submitForm();
    } else {
      goToStep(Math.min(state.currentStep + 1, STEP_LABELS.length - 1));
    }
  };

  const handleBack = () => goToStep(Math.max(state.currentStep - 1, 0));
  const isLastStep = state.currentStep === STEP_LABELS.length - 1;

  const renderStep = () => {
    switch (state.currentStep) {
      case 0:
        return (
          <SchoolProfileStep
            state={state}
            dispatch={dispatch}
            formikRef={formikRef}
            onNext={() => goToStep(1)}
          />
        );
      case 1:
        return <AcademicTermsStep state={state} dispatch={dispatch} />;
      case 2:
        return <ClassroomsStep state={state} dispatch={dispatch} />;
      case 3:
        return <FeeStructureStep state={state} dispatch={dispatch} />;
      case 4:
        return <AdminAccountsStep state={state} dispatch={dispatch} />;
      case 5:
        return <ReviewStep state={state} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper
        activeStep={state.currentStep}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{ mb: 4 }}
      >
        {STEP_LABELS.map((labelKey, i) => (
          <Step
            key={labelKey}
            completed={i < state.currentStep}
            onClick={() => i < state.currentStep && goToStep(i)}
            sx={{ cursor: i < state.currentStep ? 'pointer' : 'default' }}
          >
            <StepLabel>{t(labelKey)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box minHeight={300}>{renderStep()}</Box>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          onClick={handleBack}
          disabled={state.currentStep === 0 || submitting}
          variant="outlined"
        >
          {t('onboarding.btn_back')}
        </Button>

        {isLastStep ? (
          <LoadingButton loading={submitting} variant="contained" onClick={onComplete}>
            {t('onboarding.btn_finish')}
          </LoadingButton>
        ) : (
          <Button variant="contained" onClick={handleNext}>
            {t('onboarding.btn_next')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
